/**
 * Pure throughput / latency aggregation for the sustained-throughput run.
 *
 * Split out from the run controller so the fps + percentile math is unit
 * testable in isolation (no cap calls, no clock). Every function here is
 * pure: same inputs → same outputs, no side effects.
 */

export interface LatencyStats {
  /** 50th percentile (median) latency in ms. */
  readonly p50: number
  readonly p95: number
  readonly p99: number
  readonly mean: number
  readonly min: number
  readonly max: number
}

const EMPTY_LATENCY: LatencyStats = { p50: 0, p95: 0, p99: 0, mean: 0, min: 0, max: 0 }

/**
 * Nearest-rank percentile over an ASCENDING-sorted sample array.
 *
 * `p` is a fraction in [0, 1]. Uses the nearest-rank method: the smallest
 * value whose rank is ≥ ⌈p·n⌉. Empty input → 0. `p` is clamped to [0, 1].
 */
export function percentile(sortedAsc: readonly number[], p: number): number {
  const n = sortedAsc.length
  if (n === 0) return 0
  const clamped = p < 0 ? 0 : p > 1 ? 1 : p
  if (clamped <= 0) return sortedAsc[0] ?? 0
  if (clamped >= 1) return sortedAsc[n - 1] ?? 0
  const rank = Math.ceil(clamped * n)
  const idx = Math.min(n - 1, Math.max(0, rank - 1))
  return sortedAsc[idx] ?? 0
}

/**
 * Compute p50/p95/p99 + mean/min/max latency from a series of per-call
 * timings (ms). Input order is irrelevant — the samples are sorted
 * internally. Empty input yields all-zero stats.
 */
export function computeLatencyStats(samplesMs: readonly number[]): LatencyStats {
  if (samplesMs.length === 0) return EMPTY_LATENCY
  const sorted = [...samplesMs].sort((a, b) => a - b)
  const sum = sorted.reduce((acc, x) => acc + x, 0)
  return {
    p50: percentile(sorted, 0.5),
    p95: percentile(sorted, 0.95),
    p99: percentile(sorted, 0.99),
    mean: sum / sorted.length,
    min: sorted[0] ?? 0,
    max: sorted[sorted.length - 1] ?? 0,
  }
}

/** Frames processed per second over the measured wall-clock window. */
export function computeFps(totalFrames: number, wallMs: number): number {
  if (wallMs <= 0) return 0
  return (totalFrames * 1000) / wallMs
}

/** Detections emitted per second over the measured wall-clock window. */
export function computeDetsPerSec(totalDetections: number, wallMs: number): number {
  if (wallMs <= 0) return 0
  return (totalDetections * 1000) / wallMs
}

/**
 * Mean detections per frame — a sanity check that the fixed frame's root
 * output is stable across the run. `sampledFrames` is the number of frames
 * whose detection count was recorded.
 */
export function computeAvgDetections(totalDetections: number, sampledFrames: number): number {
  if (sampledFrames <= 0) return 0
  return totalDetections / sampledFrames
}
